import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate, useLocation } from "react-router-dom";

import PaidIcon from "@mui/icons-material/Paid";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import TimelineIcon from "@mui/icons-material/Timeline";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../components/firebase";
import { doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useDisconnect } from "wagmi";

import AuthModal from "../../components/AuthModal";
import { useWalletAuth } from "../../components/WalletConnect";

/* ===================== NAV ===================== */

const NAV_ITEMS = [
  { id: "market", label: "Predict", icon: <TimelineIcon fontSize="small" /> },
  { id: "stream", label: "Breaking", icon: <LiveTvIcon fontSize="small" /> },
  { id: "prpay", label: "PRPAY", icon: <PaidIcon fontSize="small" /> },
  { id: "profile", label: "Profile", icon: <AccountCircleIcon fontSize="small" /> },
];

const NAV_ROUTE_MAP = {
  market: "/market",
  stream: "/stream",
  prpay: "/prpay",
  profile: "/profile",
};

/* ===================== STYLES ===================== */

const ARROW_STRIP_WIDTH = 40;
const STORAGE_KEY = "pp_sidebar_collapsed";

const Sidebar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "collapsed" && prop !== "isMobile",
})(({ collapsed, isMobile }) => {
  const isMob = Boolean(isMobile);
  const panelWidth = isMob ? 74 : 90;

  const expandedWidth = panelWidth;
  const collapsedWidth = ARROW_STRIP_WIDTH;
  const width = collapsed ? collapsedWidth : expandedWidth;

  return {
    width,
    position: "fixed",
    top: "calc(50% + 40px)",
    left: 0,
    transform: "translateY(-50%)",
    zIndex: 1100,

    transition: "width 160ms ease, background-color 160ms ease, border-color 160ms ease",
    background: collapsed
      ? "transparent"
      : "linear-gradient(180deg, #020617 0%, #020617 50%, #020617 100%)",
    borderRight: collapsed ? "none" : "2px solid rgba(15,23,42,1)",

    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: collapsed ? 0 : 10,
    paddingBottom: collapsed ? 0 : 10,
    flexShrink: 0,
    pointerEvents: "auto",
  };
});

const SidebarTop = styled(Box)(() => ({
  paddingInline: 8,
  marginBottom: 10,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
}));

const SidebarNav = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  marginTop: 6,
}));

const SidebarBottom = styled(Box)(() => ({
  paddingInline: 10,
  paddingTop: 10,
  borderTop: "1px solid rgba(30,64,175,.35)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  marginTop: 10,
}));

const SidebarItemBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "collapsed",
})(({ active, collapsed }) => ({
  width: collapsed ? 48 : 64,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  paddingBlock: 6,
  borderRadius: 10,
  cursor: "pointer",
  color: active ? "#e5e7eb" : "#9ca3af",
  fontSize: 11,
  backgroundColor: active ? "rgba(37,99,235,.35)" : "transparent",
  boxShadow: active ? "0 0 0 1px rgba(59,130,246,.8)" : "none",
  border: active ? "1px solid rgba(59,130,246,.7)" : "1px solid transparent",
  "&:hover": {
    backgroundColor: active ? "rgba(37,99,235,.45)" : "rgba(15,23,42,.9)",
  },
}));

function SidebarItem({ item, active, collapsed, onClick }) {
  return (
    <Tooltip
      title={collapsed ? item.label : ""}
      placement="right"
      arrow
      disableHoverListener={!collapsed}
    >
      <SidebarItemBox active={active ? 1 : 0} collapsed={collapsed ? 1 : 0} onClick={onClick}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: active
              ? "radial-gradient(circle at 30% 0%, #60a5fa, #1d4ed8)"
              : "radial-gradient(circle at 30% 0%, #1f2937, #020617)",
            boxShadow: active ? "0 0 12px rgba(37,99,235,.9)" : "0 0 8px rgba(15,23,42,.9)",
          }}
        >
          {item.icon}
        </Box>

        {!collapsed && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 10,
              fontWeight: active ? 800 : 600,
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: ".06em",
            }}
          >
            {item.label}
          </Typography>
        )}
      </SidebarItemBox>
    </Tooltip>
  );
}

function normalizePath(p) {
  const s = String(p || "/");
  if (s === "/") return "/";
  return s.replace(/\/+$/, "");
}

const AIControlSidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const location = useLocation();

  const [user] = useAuthState(auth);
  const [role, setRole] = useState(null);

  const { disconnect } = useDisconnect();
  const { startWalletAuth } = useWalletAuth();

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // "login" | "signup"

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "1" || saved === "0") return saved === "1";
    return isMobile ? true : false;
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const offsetPx = collapsed ? 0 : (isMobile ? 80 : 104);
    root.style.setProperty("--pp-sidebar-offset", `${offsetPx}px`);
  }, [collapsed, isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved == null && isMobile) setCollapsed(true);
  }, [isMobile]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  const fetchRole = useCallback(async () => {
    if (!user) {
      setRole(null);
      return;
    }
    try {
      const snap = await getDoc(doc(db, "users", user.uid));
      setRole(snap.exists() ? snap.data().role || null : null);
    } catch {
      setRole(null);
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  const activeId = useMemo(() => {
    const p = normalizePath(location.pathname || "/");

    const best = Object.entries(NAV_ROUTE_MAP)
      .map(([id, path]) => ({ id, path: normalizePath(path) }))
      .filter(({ path }) => p === path || p.startsWith(path + "/"))
      .sort((a, b) => b.path.length - a.path.length)[0];

    if (best?.id) return best.id;

    if (p === "/" || p.startsWith("/market")) return "market";
    return "market";
  }, [location.pathname]);

  const handleLogOut = useCallback(async () => {
    try {
      disconnect?.();
      await signOut(auth);
      navigate("/market");
    } catch (e) {
      console.error("Logout failed:", e);
    }
  }, [disconnect, navigate]);

  const onNavigate = useCallback(
    (id) => {
      if (id === "profile") {
        if (!user) {
          setAuthMode("login");
          setAuthOpen(true);
          return;
        }
        navigate("/profile?tab=profile");
        return;
      }

      const route = NAV_ROUTE_MAP[id];
      if (route) navigate(route);
    },
    [navigate, user]
  );

  return (
    <>
      <Sidebar collapsed={collapsed} isMobile={isMobile}>
        <SidebarTop>
          <Tooltip title={collapsed ? "Expand" : "Collapse"} placement="right" arrow>
            <IconButton
              size="small"
              onClick={toggleCollapsed}
              sx={{
                width: collapsed ? 26 : 34,
                height: collapsed ? 80 : 44,
                borderRadius: collapsed ? 999 : 8,
                border: "1px solid rgba(148,163,184,.45)",
                bgcolor: "rgba(15,23,42,.96)",
                color: "#e5e7eb",
                boxShadow: "0 0 16px rgba(15,23,42,.85)",
                padding: 0,
                "&:hover": { bgcolor: "rgba(15,23,42,1)" },
              }}
            >
              {collapsed ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />}
            </IconButton>
          </Tooltip>
        </SidebarTop>

        {!collapsed && (
          <>
            <SidebarNav>
              {NAV_ITEMS.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  active={activeId === item.id}
                  collapsed={collapsed}
                  onClick={() => onNavigate(item.id)}
                />
              ))}
            </SidebarNav>

            <SidebarBottom>
              <Divider sx={{ width: "100%", borderColor: "rgba(255,255,255,.06)" }} />

              {user ? (
                <Tooltip title="Log out" placement="right" arrow>
                  <SidebarItemBox
                    active={0}
                    collapsed={0}
                    onClick={handleLogOut}
                    sx={{
                      width: 64,
                      border: "1px solid rgba(255,255,255,.10)",
                      background: "rgba(15,23,42,.35)",
                      "&:hover": { background: "rgba(248,113,113,.10)" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "radial-gradient(circle at 30% 0%, #1f2937, #020617)",
                        boxShadow: "0 0 10px rgba(2,8,23,.7)",
                        border: "1px solid rgba(255,255,255,.10)",
                      }}
                    >
                      <LogoutIcon fontSize="small" sx={{ color: "#e5e7eb" }} />
                    </Box>

                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 9,
                        fontWeight: 800,
                        textAlign: "center",
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        color: "#e5e7eb",
                      }}
                    >
                      Log Out
                    </Typography>
                  </SidebarItemBox>
                </Tooltip>
              ) : (
                <Tooltip title="Log in" placement="right" arrow>
                  <SidebarItemBox
                    active={0}
                    collapsed={0}
                    onClick={() => {
                      setAuthMode("login");
                      setAuthOpen(true);
                    }}
                    sx={{
                      width: 64,
                      border: "1px solid rgba(255,255,255,.10)",
                      background: "rgba(15,23,42,.35)",
                      "&:hover": { background: "rgba(15,23,42,.6)" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "radial-gradient(circle at 30% 0%, #0b1220, #020617)",
                        boxShadow: "0 0 10px rgba(2,8,23,.7)",
                        border: "1px solid rgba(255,255,255,.10)",
                      }}
                    >
                      <LoginIcon fontSize="small" sx={{ color: "#e5e7eb" }} />
                    </Box>

                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 9,
                        fontWeight: 800,
                        textAlign: "center",
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        color: "#e5e7eb",
                      }}
                    >
                      Log In
                    </Typography>
                  </SidebarItemBox>
                </Tooltip>
              )}
            </SidebarBottom>
          </>
        )}
      </Sidebar>

      <AuthModal
        open={authOpen}
        mode={authMode}
        onClose={() => setAuthOpen(false)}
        onStartWallet={() => startWalletAuth?.()}
        onSwitchMode={(nextMode) => setAuthMode(nextMode)}
      />
    </>
  );
};

export default AIControlSidebar;
